import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, type OnDestroy, type OnInit } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { PagePath } from '../../enums/page-path.enum';
import { FooterAction, type FooterLink, SubHeaderAction, type SubHeaderLink } from '../../models/footer.model';
import { DataLayerEvent } from '../../models/google-tag-manager.model';
import { type Link, LinkRoles } from '../../models/link.model';
import type { LogoConfig } from '../../models/ui.model';
import { OrderPipe } from '../../pipes/order.pipe';
import { ConfigResourceService } from '../../services/config-resource.service';
import { GoogleTagManagerService } from '../../services/google-tag-manager.service';
import { ModalService } from '../../services/modal.service';
import { RoutesUtilityService } from '../../services/routes-utility.service';
import { CmsApprovalCodeComponent } from '../cms-approval-code/cms-approval-code.component';
import { CopyrightComponent } from '../copyright/copyright.component';
import { LinkComponent } from '../link/link.component';
import { LogoComponent } from '../logo/logo.component';
import { AccessibilityStatementModalComponent } from '../modals/accessibility-statement-modal/accessibility-statement-modal.component';
import { ContactUsModalComponent } from '../modals/contact-us-modal/contact-us-modal.component';
import { FaqModalComponent } from '../modals/faq-modal/faq-modal.component';
import { PrivacyPolicyModalComponent } from '../modals/privacy-policy-modal/privacy-policy-modal.component';
import { TermsOfServiceModalComponent } from '../modals/terms-of-service-modal/terms-of-service-modal.component';
import { SiteDisclaimerComponent } from '../site-disclaimer/site-disclaimer.component';
import { WINDOW } from '../tokens/window.token';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    OrderPipe,
    LinkComponent,
    SiteDisclaimerComponent,
    CopyrightComponent,
    CmsApprovalCodeComponent,
    LogoComponent
  ]
})
export class FooterComponent implements OnInit, OnDestroy {
  private readonly window = inject(WINDOW);
  private readonly configService = inject(ConfigResourceService);
  private readonly modalService = inject(ModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly googleTagManagerService = inject(GoogleTagManagerService);

  readonly LinkRoles = LinkRoles;
  readonly footerActions: Record<FooterAction, () => void> = {
    [FooterAction.CONTACT_US]: () => {
      this.openContactUs();
    },
    [FooterAction.ACCESSIBILITY_STATEMENT]: () => {
      this.openAccessibility();
    },
    [FooterAction.TERMS_OF_USE]: () => {
      this.openTermsOfUse();
    },
    [FooterAction.PRIVACY_POLICY]: () => {
      this.openPrivacyPolicy();
    },
    [FooterAction.FAQ]: () => {
      this.openFaq();
    }
  };
  readonly subHeaderActions: Record<SubHeaderAction, () => void> = {
    [SubHeaderAction.CONTACT_US]: () => {
      this.openContactUs();
    },
    [SubHeaderAction.PRINT]: () => {
      this.printPage();
    }
  };

  currentYear = new Date().getFullYear();
  showFooter: boolean = false;
  isTopSpacerEnabled: boolean = false;
  showCopyright: boolean = true;
  footerCMSEnabled: boolean = false;
  showLastUpdated: boolean = false;
  footerLinks: FooterLink[] = [];
  subHeaderLinks: SubHeaderLink[] = [];
  copyright: string = '';
  lastUpdatedLabel: string = '';
  cmsApprovalCode: string = '';
  headerFooterDisclaimerText: string = '';
  contentServerUrl: string = '';
  footerLogoProperties: LogoConfig | undefined;
  footerLogoAlternateText: string = '';
  private readonly subscriptions: Subscription = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.configService.configLoaded$.subscribe({
        next: loaded => {
          if (loaded) {
            this.showFooter = this.configService.getProcessedResourceValue<boolean>('Spa_Site_Show_Footer', true);
            this.footerLinks = this.configService.getProcessedResourceValue<FooterLink[]>('Spa_Footer_Links', []);
            this.subHeaderLinks = this.configService.getProcessedResourceValue<SubHeaderLink[]>('Spa_HeaderFooter_SubHeaderLinks', []);
            this.isTopSpacerEnabled = this.configService.getProcessedResourceValue<boolean>('Spa_Site_Footer_TopSpacer_Enabled', false);
            this.showCopyright = this.configService.getProcessedResourceValue<boolean>('Footer_ShowCopyright', true);
            this.copyright = this.configService.getProcessedResourceValue<string>('Spa_Site_Copyright', '');
            this.headerFooterDisclaimerText = this.configService.getProcessedResourceValue<string>('Spa_HeaderFooter_Disclaimer', '');
            this.footerCMSEnabled = this.configService.getProcessedResourceValue<boolean>('Spa_Site_FooterCMS_Enabled', true);
            this.cmsApprovalCode = this.configService.getProcessedResourceValue<string>(
              'Spa_HeaderFooter_CmsApprovalCode',
              'CMS Pending Approval'
            );
            this.showLastUpdated = this.configService.getProcessedResourceValue<boolean>('Footer_ShowLastUpdated', true);
            this.lastUpdatedLabel = this.configService.getProcessedResourceValue<string>(
              'Spa_Site_LastUpdatedLabel',
              "Last updated: {{date.format([[LastUpdated]], 'MM/DD/YYYY hh:mm A')}}"
            );
            this.contentServerUrl = this.configService.getProcessedResourceValue<string>(
              'ContentServerUrl',
              'https://contentserver.destinationrx.com/ContentServer/DRxProductContent'
            );
            this.footerLogoAlternateText = this.configService.getProcessedResourceValue<string>(
              'Spa_HeaderFooter_Logo_Alternate_Text',
              'Powered by ConnectureDRX'
            );

            const siteLogoProperties = this.configService.getProcessedResourceValue<LogoConfig>('Spa_Site_LogoProperties', {
              size: 'large',
              theme: 'color'
            });
            this.footerLogoProperties = {
              theme: siteLogoProperties?.theme?.toLowerCase() ?? '',
              size: siteLogoProperties?.size?.toLowerCase() ?? ''
            };
            this.cdr.markForCheck();
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onClickFooterLink(link: FooterLink): void {
    if (link.action) {
      this.footerActions[link.action]?.();
    } else if (link.pagePath) {
      const pagePath: PagePath | null = RoutesUtilityService.convertPageNameToPagePath(link.pagePath);
      switch (pagePath) {
        case PagePath.HOME: {
          this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_HOME);
          break;
        }
        case PagePath.TERMS_OF_SERVICE: {
          this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_TERMS);
          break;
        }
        case PagePath.PRIVACY_POLICY: {
          this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_PRIVACY);
          break;
        }
        case PagePath.FAQ: {
          this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_FAQS);
          break;
        }
        case PagePath.ACCESSIBILITY_STATEMENT: {
          this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_ACCESSIBILITY);
          break;
        }
      }
    } else if (
      link.href &&
      link.href === this.configService.getProcessedResourceValue<string>('Spa_HeaderFooter_ContactUs_ExternalURL', '')
    ) {
      this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_CONTACT_US);
    }
  }

  onClickSubHeaderLink(link: SubHeaderLink): void {
    if (link.action) {
      this.subHeaderActions[link.action]?.();
    } else if (
      link.href &&
      link.href === this.configService.getProcessedResourceValue<string>('Spa_HeaderFooter_ContactUs_ExternalURL', '')
    ) {
      this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_CONTACT_US);
    }
  }

  trackByLinkTitle(index: number, link: Link): string {
    return link.title;
  }

  private openContactUs(): void {
    this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_CONTACT_US);
    this.modalService.openModal(ContactUsModalComponent, { position: { top: '1.75rem' } });
  }

  private openAccessibility(): void {
    this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_ACCESSIBILITY);
    this.modalService.openModal(AccessibilityStatementModalComponent, { width: '80%', maxWidth: '1200px' });
  }

  private openTermsOfUse(): void {
    this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_TERMS);
    this.modalService.openModal(TermsOfServiceModalComponent, { width: '80%', maxWidth: '1200px' });
  }

  private openPrivacyPolicy(): void {
    this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_PRIVACY);
    this.modalService.openModal(PrivacyPolicyModalComponent, { width: '80%', maxWidth: '1200px' });
  }

  private openFaq(): void {
    this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_FAQS);
    this.modalService.openModal(FaqModalComponent, { width: '80%', maxWidth: '1200px' });
  }

  private printPage(): void {
    this.googleTagManagerService.setClickEvent(DataLayerEvent.CLICK_GLOBAL_FOOTER_PRINT_PAGE);
    this.window.print();
  }
}
